import { useQuery } from '@tanstack/react-query';
import { motion } from 'framer-motion';
import { FC } from 'react';
import styled from 'styled-components';

import { CloseButton } from '@/components/AlertDialogs/Dialog';
import ContentSpinner from '@/components/ContentSpinner';
import { Modal } from '@/components/Modal';
import Text from '@/components/Text';
import useAlerts from '@/hooks/useAlerts';
import { api } from '@/utils/api';
import { numFormatter } from '@/utils/helpers';

const Dialog = styled(motion.div)`
  width: 1120px;
  border-radius: 4px;
  padding: 56px 24px 24px;
  background: ${({ theme }) => theme.colors.white};
  position: relative;
`;

const StyledContentSpinner = styled(ContentSpinner)`
  margin: 100px 0;
`;

const Row = styled.div`
  display: flex;
  gap: 24px;
  align-items: flex-start;
`;

const Thumbnail = styled.img`
  width: 342px;
  height: auto;
  margin-right: 24px;
  flex-shrink: 0;
`;

const Content = styled.div`
  flex: 1;
`;

const Title = styled(Text)`
  margin-bottom: 12px;
`;

const InfoBlockTitle = styled(Text)`
  margin-bottom: 8px;
`;

const InfoBlock = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 126px);
  grid-row-gap: 16px;
  > div {
    padding-right: 10px;
  }
`;

const ChannelTitle = styled(Text)`
  display: flex;
  align-items: center;
  margin: 0 0 34px;
  > img {
    border: 1px solid rgba(144, 144, 144, 0.3);
    border-radius: 50%;
    width: 16px;
    height: 16px;
    flex-shrink: 0;
    object-fit: cover;
    margin-right: 6px;
  }
`;

interface Props {
  onClose: () => void;
  externalVideoId: string;
}

const VideoReferenceModal: FC<Props> = ({ onClose, externalVideoId }) => {
  const alerts = useAlerts();

  const { data: video, isLoading } = useQuery(
    ['production_video_reference', { id: externalVideoId }],
    () =>
      api.get<
        never,
        {
          title: string;
          thumbnail: string;
          channel: {
            name: string;
            thumbnail: string;
            subscribers: number;
          };
          views: number;
          publishedAt: string;
          vph?: number;
          v7?: number;
        }
      >(`/v1/production/video-reference/${externalVideoId}`),
    {
      staleTime: 120 * 1000,
      onError: () =>
        alerts.error('Failed to fetch video details for provided url'),
      retry: 2,
    },
  );

  return (
    <Modal>
      <Dialog
        initial={{ scale: 0.3 }}
        animate={{ scale: 1 }}
        exit={{ scale: 0.3 }}
      >
        <CloseButton onClick={onClose}>
          <svg
            width="14"
            height="14"
            viewBox="0 0 14 14"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M13 1L1 13"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <path
              d="M13 13L1 1"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </CloseButton>
        {isLoading || !video ? (
          <StyledContentSpinner />
        ) : (
          <Row>
            <Thumbnail src={video.thumbnail} alt="Video thumbnail" />
            <Content>
              <Title $type="button">{video.title}</Title>
              <ChannelTitle $type="smallNormal">
                <img src={video.channel.thumbnail} alt="Channel icon" />
                {video.channel.name}
              </ChannelTitle>
              <InfoBlock>
                <div>
                  <InfoBlockTitle $type="smallBold">Subscribers</InfoBlockTitle>
                  <Text $type="smallNormal">
                    {video.channel.subscribers
                      ? numFormatter(video.channel.subscribers, 1)
                      : '-'}
                  </Text>
                </div>
                <div>
                  <InfoBlockTitle $type="smallBold">Video Views</InfoBlockTitle>
                  <Text $type="smallNormal">
                    {video.views ? numFormatter(video.views, 1) : '-'}
                  </Text>
                </div>
                <div>
                  <InfoBlockTitle $type="smallBold">Date Added</InfoBlockTitle>
                  <Text $type="smallNormal">{video.publishedAt}</Text>
                </div>
                <div>
                  <InfoBlockTitle $type="smallBold">VPH7</InfoBlockTitle>
                  <Text $type="smallNormal">
                    {video.vph ? numFormatter(video.vph, 1) : '-'}
                  </Text>
                </div>
                <div>
                  <InfoBlockTitle $type="smallBold">V7</InfoBlockTitle>
                  <Text $type="smallNormal">
                    {video.v7 ? numFormatter(video.v7, 1) : '-'}
                  </Text>
                </div>
              </InfoBlock>
            </Content>
          </Row>
        )}
      </Dialog>
    </Modal>
  );
};

export default VideoReferenceModal;
